import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';
import styled from 'styled-components';

import ArrowIosRight from '@crehana/icons/outline/ArrowIosRight';
import { useOnClickOutside } from '@crehana/react-hooks';
import {
  CuiOptionType,
  KEY_NAMES,
  parseClassnames,
  TextFieldBackgroundState,
  TextFieldBorderState,
  transition,
} from '@crehana/ui';

import Menu from '../Menu';
import {
  TEXT_FIELD_BACKGROUND_COLOR_CLASSNAMES,
  TEXT_FIELD_BORDER_COLOR_CLASSNAMES,
} from './classnames';
import { DropdownProps } from './types';

const arrowStyles = {
  bottom: (open: boolean) =>
    open ? 'transform: rotate(-90deg);' : 'transform: rotate(90deg)',
  top: (open: boolean) =>
    !open ? 'transform: rotate(-90deg);' : 'transform: rotate(90deg)',
};

const Arrow = styled(ArrowIosRight)<{
  open: boolean;
  displayPosition: 'bottom' | 'top' | 'responsive';
}>`
  transition: ${transition('transform')};
  ${({ theme, open, displayPosition }) =>
    displayPosition === 'responsive'
      ? `
        ${arrowStyles.top(open)};
        ${theme.breakpoints.up('sm')} {
          ${arrowStyles.bottom(open)};
        }
      `
      : arrowStyles[displayPosition](open)}
`;

function Dropdown<OptionValueType = string>({
  options,
  selected,
  onSelected,
  label,
  labelProps = {},
  hint,
  required,
  disabled,
  full,
  sm,
  size = 4,
  open: openProp,
  onToggleOpen: onToggleOpenProp,
  selectId,
  name,
  form,
  className = '',
  clampText = true,
  isDark,
  dark,
  isTransparency,
  transparencyMode,
  onOpen,
  'data-testid': dataTestId,
  displayPosition = 'bottom',
  // TODO: talk with the code owner to fix this code so we can enable this rule again

  inputRef,
  error,
  errorText,
  htmlProps,
  ...other
}: DropdownProps<OptionValueType>) {
  const OPTION_HEIGHT = sm ? 34 : 56;
  const [openState, setOpenState] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const setSelectElementValue = (newValue: string) => {
    if (selectId) {
      const selectElement = document.getElementById(selectId);

      if (selectElement) {
        (selectElement as HTMLSelectElement).value = newValue;
      }
    } else if (name) {
      const namedElements = Array.from(document.getElementsByName(name));
      const element = namedElements.find(e => e.tagName === 'SELECT');

      if (element) {
        (element as HTMLSelectElement).value = newValue;
      }
    }
  };

  useEffect(() => {
    if (
      (selectId || name) &&
      (typeof selected?.value === 'string' ||
        typeof selected?.value === 'number')
    ) {
      setSelectElementValue(selected.value.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasOpenProp = typeof openProp === 'boolean';

  const onToggleOpen = () => {
    if (!disabled) {
      const newOpenState = !openState;

      if (!hasOpenProp) {
        setOpenState(newOpenState);
        if (newOpenState && onOpen) {
          onOpen();
        }
      }
      if (typeof onToggleOpenProp === 'function') {
        onToggleOpenProp(!hasOpenProp ? newOpenState : undefined);
      }
    }
  };

  const handler = () => {
    if (openState || openProp) {
      onToggleOpen();
    }
  };

  useOnClickOutside(containerRef, handler);

  const onSelect = (option: CuiOptionType<OptionValueType>) => {
    const currentlySelected = selected && selected.value === option.value;
    let newSelected: CuiOptionType<OptionValueType> | undefined = option;

    if (!required && currentlySelected) {
      newSelected = undefined;
    }

    if (typeof onSelected === 'function' && selected !== newSelected) {
      onSelected(newSelected);
    }
    if (selectId || name) {
      setSelectElementValue(
        typeof selected?.value === 'string' ||
          typeof selected?.value === 'number'
          ? selected.value.toString()
          : '',
      );
    }
    onToggleOpen();
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    action: () => void,
  ) => {
    if (event.key === KEY_NAMES.ENTER) {
      event.stopPropagation();
      action();
    }
  };

  const open = typeof openProp === 'boolean' ? openProp : openState;

  const shownOptions = options.length >= size ? size : options.length;
  const selectedLabel = selected?.jsxLabel || selected?.label;

  const wrapperPositionClassNames = {
    bottom: 'flex-col',
    top: 'flex-col-reverse absolute bottom-0 inset-x-0',
    responsive:
      'flex-col-reverse absolute bottom-0 sm:flex-col sm:bottom-auto sm:top-0',
  };
  let mainFontColor =
    isDark || dark || isTransparency || transparencyMode
      ? 'text-white'
      : 'text-base-main';

  let backgroundState: TextFieldBackgroundState =
    isDark || dark ? 'dark' : 'light';
  let borderState: TextFieldBorderState = isDark || dark ? 'dark' : 'light';

  if (isTransparency || transparencyMode) {
    backgroundState = 'transparency';
    borderState = 'transparency';

    if (openState || openProp) {
      borderState = 'transparency-focused';
      backgroundState = 'transparency-focused';
    }

    if (disabled) {
      backgroundState = 'transparency-disabled';
      borderState = 'disabled';
      mainFontColor = 'text-gray-dark';
    }
  } else {
    if (openState || openProp) {
      borderState = isDark || dark ? 'dark-focused' : 'light-focused';
    }
    if (disabled) {
      backgroundState = isDark || dark ? 'dark-disabled' : 'light-disabled';
      borderState = 'disabled';
      mainFontColor = 'text-gray-dark';
    }
  }

  if (error || errorText) {
    borderState = 'error';
  }

  if (!selectedLabel) {
    mainFontColor =
      isDark || dark || isTransparency || transparencyMode
        ? 'text-base-lighter'
        : 'text-gray-dark';
  }

  return (
    <div
      className={parseClassnames(
        className,
        cn('inline-flex flex-col', full ? 'w-full' : 'w-full sm:w-280'),
      )}
      ref={containerRef}
      {...other}
      {...htmlProps}
    >
      {label && (
        <label
          className={cn(
            'font-poppins font-semibold text-16 mb-8',
            isDark || dark || isTransparency || transparencyMode
              ? 'text-white'
              : 'text-black',
          )}
          {...labelProps}
        >
          {label}
        </label>
      )}
      <select
        className="hidden"
        name={name}
        id={selectId}
        form={form}
        data-testid={dataTestId}
      >
        {hint && (
          <option value="" disabled>
            {hint}
          </option>
        )}
        {options.map(opt => (
          <option
            key={`${selectId || name}-${opt.value}`}
            value={
              typeof opt.value === 'string' || typeof opt.value === 'number'
                ? opt.value
                : `${selectId || name}-${opt.value}`
            }
          >
            {opt.label}
          </option>
        ))}
      </select>
      <div className={cn('relative', sm ? 'h-32' : 'h-56')}>
        <div
          className={cn(
            'inline-flex w-full z-1',
            sm ? 'h-32' : 'h-56',
            disabled ? 'cursor-default' : 'cursor-pointer',
            wrapperPositionClassNames[displayPosition],
            {
              'shadow-dropdown':
                open && !(isDark || dark || isTransparency || transparencyMode),
            },
          )}
          style={{
            transition: transition([
              'box-shadow',
              'background-color',
              'border',
            ]),
            height: `${
              (open ? OPTION_HEIGHT * (shownOptions + 1) : OPTION_HEIGHT) +
              (!(isDark || dark || isTransparency || transparencyMode) ? 2 : 0)
            }px`,
            zIndex: open ? 200 : 2,
          }}
        >
          <div
            className={cn(
              'inline-flex flex-shrink-0 justify-between items-center w-full rounded-10 border border-solid px-16',
              sm ? 'h-32' : 'h-56',
              TEXT_FIELD_BACKGROUND_COLOR_CLASSNAMES[backgroundState],
              TEXT_FIELD_BORDER_COLOR_CLASSNAMES[borderState],
            )}
            onClick={onToggleOpen}
            onKeyDown={e => handleKeyDown(e, onToggleOpen)}
            data-testid={dataTestId ? `${dataTestId}-button` : undefined}
          >
            <span
              className={cn(
                'font-poppins font-normal text-16 overflow-hidden pr-16',
                mainFontColor,
                {
                  'truncate break-all': sm || clampText,
                },
              )}
              data-testid={dataTestId ? `${dataTestId}-label` : undefined}
            >
              {selectedLabel || hint}
            </span>
            <Arrow
              className={cn('fill-current', {
                [isDark || dark || isTransparency || transparencyMode
                  ? 'text-secondary-main'
                  : 'text-primary-main']: !disabled,
                [mainFontColor]: disabled,
              })}
              ignoreColor
              size={24}
              displayPosition={displayPosition}
              open={open}
            />
          </div>
          {open && (
            <Menu
              className="z-7 font-poppins text-24"
              options={options}
              selected={selected}
              onSelected={onSelect}
              full
              clampText={clampText}
              isDark={isDark || dark}
              isTransparency={isTransparency || transparencyMode}
              data-testid={dataTestId ? `${dataTestId}-options` : undefined}
            />
          )}
        </div>
      </div>
      {errorText && (
        <span
          className={cn(
            'font-caption mt-8',
            disabled ? 'text-gray-dark' : 'text-red-main',
          )}
          role="alert"
        >
          {errorText}
        </span>
      )}
    </div>
  );
}

export default Dropdown;
